import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useDataRefresh } from "@/hooks/useDataRefresh";
import { toast } from "sonner";
import { CalendarRange, RefreshCw, GraduationCap, MessageCircle } from "lucide-react";

// V1.83 · Cronograma de clases del alumno: una fila por cita (no por sesión),
// con nº de clases, fecha, hora inicio/fin, confirmación y estado.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function classCount(a) {
  const { time: start, end_time: end } = a;
  if (!start || !end || end <= start) return 1;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return Math.max(1, Math.floor(((eh * 60 + em) - (sh * 60 + sm)) / 45));
}

function stateOf(a) {
  if (a.status === "cancelled") {
    return a.cancel_consumes
      ? { label: "Cancelada Resta", cls: "text-red-700" }
      : { label: "Cancelada No Resta", cls: "text-emerald-700" };
  }
  if (a.attended === true) return { label: "Realizada", cls: "text-blue-700" };
  if (a.attended === false && a.date < todayISO()) {
    return a.missed_exempt
      ? { label: "No asistió · no resta", cls: "text-emerald-700" }
      : { label: "No asistió", cls: "text-amber-700" };
  }
  return { label: "Programada", cls: "text-neutral-700" };
}

const confirmOf = (a) =>
  a.status === "confirmed" ? "Confirmada" : a.status === "change_requested" ? "Cambio solicitado" : "No confirmada";

const fmt = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}-${m}-${y}`;
};

export default function StudentClassChronogram({ studentId, student }) {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/appointments`, {
        params: { student_id: studentId, include_shared: true },
      });
      setAppts(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);
  useDataRefresh(load);

  const { classes, exams, totalClases } = useMemo(() => {
    const byDate = (a, b) => (a.date || "").localeCompare(b.date || "") || (a.time || "").localeCompare(b.time || "");
    const cls = appts
      .filter((a) => !String(a.appointment_type || "clase_practica").startsWith("examen"))
      .sort(byDate);
    const ex = appts
      .filter((a) => String(a.appointment_type || "").startsWith("examen")
        && a.status !== "cancelled" && (a.date || "") >= todayISO())
      .sort(byDate);
    // Sólo las clases prácticas descuentan del bono (igual que el backend).
    const consumed = cls.reduce((acc, a) => {
      if ((a.appointment_type || "clase_practica") !== "clase_practica") return acc;
      if (a.status === "cancelled" && !a.cancel_consumes) return acc;
      if (a.missed_exempt && a.attended !== true) return acc;
      return acc + classCount(a);
    }, 0);
    return { classes: cls, exams: ex, totalClases: consumed };
  }, [appts]);

  const visible = showAll ? classes : classes.slice(-12);

  // V1.83b · Enviar el cronograma por WhatsApp (próximas o historial completo)
  // para que el alumno pueda cotejar sus clases si hay discrepancias.
  const sendWhatsapp = (scope) => {
    const phone = String(student?.phone || "").replace(/\D/g, "");
    if (!phone) {
      toast.error("El alumno no tiene teléfono guardado");
      return;
    }
    const today = todayISO();
    const rows = scope === "next" ? classes.filter((a) => (a.date || "") >= today) : classes;
    if (rows.length === 0) {
      toast.error(scope === "next" ? "No hay clases próximas" : "No hay clases registradas");
      return;
    }
    const title = scope === "next" ? "PRÓXIMAS CLASES" : "CRONOGRAMA DE CLASES";
    const lines = rows.map((a) => {
      const n = classCount(a);
      return `• ${fmt(a.date)} ${a.time || ""}-${a.end_time || ""} · ${n} ${n === 1 ? "clase" : "clases"} · ${stateOf(a).label}`;
    });
    const exLines = exams.slice(0, 6).map(
      (a) => `• ${fmt(a.date)} ${a.time || ""} · ${a.appointment_type === "examen_teorico" ? "Teórico" : "Práctico"} · ${confirmOf(a)}`
    );
    let body = `*${title}*\n${(student?.name || "").trim()}\n\n${lines.join("\n")}`;
    if (scope !== "next") body += `\n\n*Total clases prácticas consumidas:* ${totalClases}`;
    if (exLines.length) body += `\n\n*PRÓXIMOS EXÁMENES*\n${exLines.join("\n")}`;
    body += `\n\nSi ves cualquier discrepancia, dínoslo y lo revisamos.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(body)}`, "_blank", "noopener");
  };

  return (
    <section className="pt-4 border-t border-neutral-100" data-testid="student-chronogram">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CalendarRange className="h-4 w-4 text-orange-600" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-neutral-700">Cronograma de clases</h3>
        <span className="text-xs text-neutral-500 font-mono">
          {totalClases} {totalClases === 1 ? "clase práctica" : "clases prácticas"} consumidas
        </span>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button
            onClick={() => sendWhatsapp("next")}
            data-testid="chronogram-wa-next"
            title="Enviar al alumno sus próximas clases por WhatsApp"
            className="h-8 px-2.5 rounded-lg text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1.5"
          >
            <MessageCircle className="h-3.5 w-3.5" /> Próximas
          </button>
          <button
            onClick={() => sendWhatsapp("all")}
            data-testid="chronogram-wa-all"
            title="Enviar al alumno todo el cronograma por WhatsApp"
            className="h-8 px-2.5 rounded-lg text-xs font-semibold border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50 inline-flex items-center gap-1.5"
          >
            <MessageCircle className="h-3.5 w-3.5" /> Todas
          </button>
          {classes.length > 12 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              data-testid="chronogram-toggle-all"
              className="text-xs font-semibold text-orange-700 hover:underline"
            >
              {showAll ? "Ver solo las últimas 12" : `Ver todas (${classes.length})`}
            </button>
          )}
          <button onClick={load} data-testid="chronogram-reload" className="btn-secondary !h-8 !text-xs">
            <RefreshCw className="h-3.5 w-3.5" /> Actualizar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-3">Cargando cronograma…</div>
      ) : classes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-neutral-200 py-6 text-center text-sm text-neutral-500"
          data-testid="chronogram-empty">
          Sin clases registradas.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200">
          <table className="w-full text-sm" data-testid="chronogram-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 bg-neutral-50 border-b border-neutral-200">
                <th className="py-2 px-3 text-right">Clases</th>
                <th className="py-2 px-3">Tipo</th>
                <th className="py-2 px-3">Fecha</th>
                <th className="py-2 px-3">Hora inicio</th>
                <th className="py-2 px-3">Hora fin</th>
                <th className="py-2 px-3">Confirmación</th>
                <th className="py-2 px-3">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {visible.map((a) => {
                const st = stateOf(a);
                return (
                  <tr key={a.id} data-testid={`chronogram-row-${a.id}`} className="hover:bg-orange-50/40">
                    <td className="py-1.5 px-3 text-right font-mono tabular-nums font-semibold">{classCount(a)}</td>
                    <td className="py-1.5 px-3 text-xs">
                      {(a.appointment_type || "clase_practica") === "clase_practica" ? "Práctica" : "Teórica"}
                    </td>
                    <td className="py-1.5 px-3 font-mono tabular-nums">{fmt(a.date)}</td>
                    <td className="py-1.5 px-3 font-mono tabular-nums">{a.time || "—"}</td>
                    <td className="py-1.5 px-3 font-mono tabular-nums">{a.end_time || "—"}</td>
                    <td className="py-1.5 px-3 text-xs">{confirmOf(a)}</td>
                    <td className={"py-1.5 px-3 text-xs font-semibold " + st.cls}>{st.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-2 mt-5 mb-2">
        <GraduationCap className="h-4 w-4 text-orange-600" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-neutral-700">Próximos exámenes</h3>
        {exams.length > 6 && (
          <span className="text-xs text-neutral-500">mostrando los 6 más próximos de {exams.length}</span>
        )}
      </div>
      {exams.length === 0 ? (
        <div className="rounded-xl border border-dashed border-neutral-200 py-4 text-center text-sm text-neutral-500"
          data-testid="chronogram-exams-empty">
          Sin exámenes convocados.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200">
          <table className="w-full text-sm" data-testid="chronogram-exams-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 bg-neutral-50 border-b border-neutral-200">
                <th className="py-2 px-3 text-right w-16">Nº</th>
                <th className="py-2 px-3">Fecha</th>
                <th className="py-2 px-3">Hora</th>
                <th className="py-2 px-3">Confirmación</th>
                <th className="py-2 px-3">Tipo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {exams.slice(0, 6).map((a) => (
                <tr key={a.id} data-testid={`chronogram-exam-row-${a.id}`} className="hover:bg-orange-50/40">
                  <td className="py-1.5 px-3 text-right font-mono tabular-nums font-semibold w-16">1</td>
                  <td className="py-1.5 px-3 font-mono tabular-nums">{fmt(a.date)}</td>
                  <td className="py-1.5 px-3 font-mono tabular-nums">{a.time || "—"}</td>
                  <td className="py-1.5 px-3 text-xs">{confirmOf(a)}</td>
                  <td className="py-1.5 px-3 text-xs font-semibold text-neutral-800">
                    {a.appointment_type === "examen_teorico" ? "Teórico" : "Práctico"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
